(() => {
  const init = (scope = document) => {
    scope.querySelectorAll('[data-product-question-form]').forEach((form) => {
      if (form.dataset.questionReady === 'true') return;
      form.dataset.questionReady = 'true';
      form.dataset.questionTransport = 'fetch';
      const notice = form.querySelector('[data-question-notice]');
      const button = form.querySelector('button[type="submit"]');
      const buttonText = button.textContent;
      let pending = false;
      let posting = false;
      let attempt = 0;
      let timeout;
      let controller;

      const message = (text, state) => {
        notice.textContent = text;
        notice.dataset.state = state;
        notice.setAttribute('role', state === 'error' ? 'alert' : 'status');
        notice.hidden = false;
      };
      const finish = (text, state) => {
        clearTimeout(timeout);
        pending = false;
        posting = false;
        form.removeAttribute('aria-busy');
        button.disabled = false;
        button.textContent = buttonText;
        message(text, state);
      };

      // Install the transport before Shopify binds CAPTCHA. Shopify wraps this
      // submit method, adds its token, then calls it after verification. A frame
      // cannot receive the response because Shopify sends frame-ancestors 'none'.
      form.submit = async () => {
        if (!pending || posting) return;
        posting = true;
        const currentAttempt = attempt;
        controller = new AbortController();
        try {
          const action = new URL(form.action, location.href);
          if (action.origin !== location.origin) throw new Error('Invalid contact destination.');
          const body = new URLSearchParams();
          for (const [key, value] of new FormData(form)) {
            if (typeof value === 'string') body.append(key, value);
          }
          const response = await fetch(action.href, {
            method: 'POST', body, credentials: 'same-origin',
            headers: { Accept: 'text/html' }, signal: controller.signal,
          });
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!pending || currentAttempt !== attempt) return;
          const url = new URL(response.url || action.href);
          const success = html.querySelector('[data-question-result="success"]');
          const errors = html.querySelector('[data-question-result="error"], .errors');
          if (response.ok && url.origin === location.origin &&
              (url.searchParams.get('contact_posted') === 'true' || success)) {
            form.reset();
            finish('✓ Your question has been sent. Our product team will reply within 1 working day.', 'success');
          } else if (url.origin === location.origin && /\/challenge\/?$/.test(url.pathname)) {
            finish('Please complete Shopify’s security check to send your question.', 'challenge');
            const link = document.createElement('a');
            link.href = url.href;
            link.textContent = 'Complete security check';
            // Shopify's challenge needs the current tab's form session data.
            // Navigate only when the customer explicitly chooses the check.
            notice.append(document.createTextNode(' '), link);
          } else {
            finish(errors?.textContent.trim() ||
              'Your question could not be sent. Please try again or contact support.', 'error');
          }
        } catch {
          if (pending && currentAttempt === attempt) finish(
            'We could not confirm that your question was sent. Please try again or contact support.', 'error'
          );
        }
      };
      // Shopify binds protected contact forms on focus/change, and the submit
      // handler below also waits for its CAPTCHA bridge. Do not force the hidden
      // question form to download and run the provider on every product visit.

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (pending) {
          event.stopImmediatePropagation();
          return;
        }
        pending = true;
        posting = false;
        attempt += 1;
        const currentAttempt = attempt;
        form.setAttribute('aria-busy', 'true');
        button.disabled = true;
        button.textContent = 'Sending…';
        message('Sending your question…', 'sending');
        timeout = setTimeout(() => {
          controller?.abort();
          if (pending && currentAttempt === attempt) finish(
            'We could not confirm that your question was sent. Please try again or contact support.', 'error'
          );
        }, 60000);
        try {
          const protect = window.Shopify?.captcha?.protect;
          if (typeof protect === 'function') {
            const wasBound = form.dataset.hcaptchaBound === 'true' || form.dataset.recaptchaBound === 'true';
            // Use Shopify's supported bridge; never call the CAPTCHA provider API.
            protect(form, () => {
              if (!pending || currentAttempt !== attempt) return;
              if (!wasBound) {
                // Replay the native submit once after binding. Shopify's own
                // document listener persists the form key before its wrapped
                // submit method obtains the CAPTCHA token and calls our AJAX
                // transport. This also covers keyboard/programmatic submits
                // with no prior focus in the form.
                clearTimeout(timeout);
                pending = false;
                form.requestSubmit();
              } else form.submit();
            });
          } else {
            void form.submit();
          }
        } catch {
          finish('The security check could not load. Please try again or contact support.', 'error');
        }
      }, true);
    });
  };
  // Initialize during deferred script execution, before automatic CAPTCHA
  // binding on DOMContentLoaded, rather than waiting for that event ourselves.
  init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
